import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Collapse, TableCell, TableRow } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { put } from 'helpers/apiHelpers';
import { isGranted } from 'helpers/helpers';
import { ROLE_EDIT_BRAND_ACCESS_KEY } from 'helpers/roles';
import { TranslatableObjectKey } from 'hooks/redux/Translatable/useTranslatableLanguages';
import LabeledCheckbox from 'components/Checkbox/LabeledCheckbox';
import RegularButton from 'components/CustomButtons/Button';
import SweetAlertConfirmation from 'components/SweetAlertConfirmation/SweetAlertConfirmation';

import useGetConsentsDataFromBrandTerm from '../hooks/useGetConsentsDataFromBrandTerm';
import ConsentsDecisionModal from './ConsentsDecisionModal/ConsentsDecisionModal';
import LangFormTextInput from '../../LangFormTextInput/LangFormTextInput';
import TermsHeader from '../../TermsHeader/TermsHeader';

const useStyles = makeStyles({
  activeCheckbox: {
    margin: '0',
  },
});

const MarketingConsentsRow = ({
  name,
  consentsData,
  setConsentsData,
  consentLabel,
  additionalInfoInfoLabel,
  additionalContentLabel,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { clientLanguages } = useSelector(({ Auth }) => Auth);

  const getConsentsDataFromBrandTerm = useGetConsentsDataFromBrandTerm();

  const [isConsentsDecisionModalOpen, setIsConsentsDecisionModalOpen] =
    useState(false);
  const [isDisableAlertConfirmationOpen, setIsDisableAlertConfirmationOpen] =
    useState(false);

  const initialConsents = {
    consent: consentsData.consent,
    active: consentsData.active,
    additionalInfo: consentsData.additionalInfo,
    additionalContent: consentsData.additionalContent,
  };

  const [consents, setConsents] = useState(initialConsents);
  const [missingConsent, setMissingConsent] = useState({
    additionalContent: {},
    consent: {},
  });

  const { consent, active, additionalInfo, additionalContent } = consents;

  const consentId = consentsData.id;

  const isDisabled = !isGranted(ROLE_EDIT_BRAND_ACCESS_KEY);

  const openConsentsDecisionModal = () => {
    setIsConsentsDecisionModalOpen(true);
  };

  const closeDisableAlertConfirmation = () => {
    setIsDisableAlertConfirmationOpen(false);
  };

  const handleUpdateBrandTerms = async payload => {
    try {
      const brandTerm = await put(`/brand-terms/${consentId}`, payload, {
        params: {
          translations: true,
        },
      });

      setConsentsData(getConsentsDataFromBrandTerm(brandTerm));

      toast.success(t('brandCfg.marketingConsentUpdated'));
      // eslint-disable-next-line no-empty -- FIXME
    } catch {}
  };

  return (
    <>
      <TableRow>
        <TableCell>{name}</TableCell>
        <TableCell align="center">
          <LabeledCheckbox
            className={classes.activeCheckbox}
            disabled={isDisabled}
            checked={active}
            onChange={e => {
              setConsents(prev => ({
                ...prev,
                active: e.target.checked,
              }));
            }}
          />
        </TableCell>
        <TableCell>
          <TermsHeader text={consentLabel} />
          {clientLanguages.map(({ id, isoCode }) => (
            <LangFormTextInput
              key={id}
              lang={isoCode}
              disabled={isDisabled}
              isError={missingConsent.consent[isoCode]}
              value={consent[isoCode]}
              handleChange={e => {
                setMissingConsent(prev => {
                  const { [isoCode]: _, ...missingConsentWithoutIsoCode } =
                    prev.consent;

                  return {
                    ...prev,
                    consent: missingConsentWithoutIsoCode,
                  };
                });
                setConsents(prev => ({
                  ...prev,
                  consent: {
                    ...prev.consent,
                    [isoCode]: e.target.value,
                  },
                }));
              }}
              multiline={true}
              rows={2}
              rowsMax={20}
              maxLength={4000}
            />
          ))}
          <LabeledCheckbox
            disabled={isDisabled}
            label={additionalInfoInfoLabel}
            checked={additionalInfo}
            onChange={e => {
              setConsents(prev => ({
                ...prev,
                additionalInfo: e.target.checked,
              }));
            }}
          />
          <Collapse in={additionalInfo}>
            <div>
              <TermsHeader text={additionalContentLabel} />
              {clientLanguages.map(({ id, isoCode }) => (
                <LangFormTextInput
                  key={id}
                  lang={isoCode}
                  disabled={isDisabled}
                  isError={missingConsent.additionalContent[isoCode]}
                  value={additionalContent[isoCode]}
                  handleChange={e => {
                    setMissingConsent(prev => {
                      const { [isoCode]: _, ...missingConsentWithoutIsoCode } =
                        prev.additionalContent;

                      return {
                        ...prev,
                        additionalContent: missingConsentWithoutIsoCode,
                      };
                    });
                    setConsents(prev => ({
                      ...prev,
                      additionalContent: {
                        ...prev.additionalContent,
                        [isoCode]: e.target.value,
                      },
                    }));
                  }}
                  multiline={true}
                  rows={2}
                  rowsMax={20}
                  maxLength={4000}
                />
              ))}
            </div>
          </Collapse>
        </TableCell>
        <TableCell>
          <RegularButton
            disabled={
              isDisabled ||
              JSON.stringify(consents) === JSON.stringify(initialConsents)
            }
            color="success"
            onClick={async () => {
              const currentMissingConsent = {
                additionalContent: {},
                consent: {},
              };

              clientLanguages.forEach(({ isoCode }) => {
                if (
                  additionalInfo &&
                  !consents.additionalContent[isoCode].trim()
                ) {
                  currentMissingConsent.additionalContent[isoCode] = true;
                }
                if (active && !consents.consent[isoCode].trim()) {
                  currentMissingConsent.consent[isoCode] = true;
                }
              });

              if (
                Object.keys(currentMissingConsent.additionalContent).length >
                  0 ||
                Object.keys(currentMissingConsent.consent).length > 0
              ) {
                setMissingConsent(currentMissingConsent);

                toast.error(t('brandCfg.error.serviceTerms.fillMissingFields'));

                return;
              }

              if (consentsData.active && !active) {
                setIsDisableAlertConfirmationOpen(true);
                // eslint-disable-next-line no-negated-condition -- FIXME
              } else if (
                JSON.stringify(consentsData.consent) !== JSON.stringify(consent)
              ) {
                openConsentsDecisionModal();
              } else {
                await handleUpdateBrandTerms({
                  active,
                  additionalContent: {
                    [TranslatableObjectKey]: additionalContent,
                  },
                  additionalInfo,
                });
              }
            }}
          >
            {t('common.shared.save')}
          </RegularButton>
        </TableCell>
      </TableRow>
      {!isDisabled && (
        <>
          {isDisableAlertConfirmationOpen && (
            <SweetAlertConfirmation
              title={t('brandCfg.consentsDisableConfirmation')}
              onCancel={closeDisableAlertConfirmation}
              onConfirm={async () => {
                closeDisableAlertConfirmation();

                // eslint-disable-next-line no-negated-condition -- FIXME
                if (
                  JSON.stringify(consentsData.consent) !==
                  JSON.stringify(consent)
                ) {
                  openConsentsDecisionModal();
                } else {
                  await handleUpdateBrandTerms({
                    active,
                    additionalContent: {
                      [TranslatableObjectKey]: additionalContent,
                    },
                    additionalInfo,
                  });
                }
              }}
            >
              {t('brandCfg.consentsDisableConfirmationInfo')}
            </SweetAlertConfirmation>
          )}
          <ConsentsDecisionModal
            isOpen={isConsentsDecisionModalOpen}
            onClose={() => {
              setIsConsentsDecisionModalOpen(false);
            }}
            onSave={async userAgreementAction => {
              await handleUpdateBrandTerms({
                userAgreementAction,
                active,
                additionalContent: {
                  [TranslatableObjectKey]: additionalContent,
                },
                additionalInfo,
                consent: {
                  [TranslatableObjectKey]: consent,
                },
              });
            }}
          />
        </>
      )}
    </>
  );
};

export default MarketingConsentsRow;
